import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  DateInput,
  FormContainer,
  FormInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { useCallback, useState } from "react";
import {
  createTicketAutoInactiveRule,
  getTicketAutoInactiveRuleById,
  updateTicketAutoInactiveRuleById,
} from "../../../../../../services/ticketAutoInactiveRule.service";
import TicketWorkspace from "./TicketWorkspace";
import TicketChannel from "./TicketChannel";
import { TicketChannelStep } from "./TicketChannelStep";
import {
  ticketAutoInactiveRuleSchema,
  TTicketAutoInactiveRuleForm,
} from "./TicketAutoInactiveRuleSchema";
import { TicketType } from "./TicketType";

const translationPath = "page.settings.crm.ticketAutoInactivityRules.";

const TicketAutoInactiveRule = () => {
  const { t } = useTranslation();

  const { sendRequest: save, loading: sLoading } = useFetch(
    createTicketAutoInactiveRule,
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateTicketAutoInactiveRuleById,
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getTicketAutoInactiveRuleById,
  );

  const [ticketWorkspaceId, setTicketWorkspaceId] = useState("");

  const [message, setMessage] = useState("");

  const handleSave = (v: TTicketAutoInactiveRuleForm) => {
    return save({
      ...v,
      ticketChannelId: v.ticketChannel ? v.ticketChannel.id : "",
      ticketChannelStepId: v.ticketChannelStep?.id!,
      ticketTypeId: v.ticketType.id,
      minutes: Number(v.minutes),
      initialDate: v.initialDate.toISOString(),
      endDate: v.endDate.toISOString(),
    });
  };

  const updateHandler = (v: TTicketAutoInactiveRuleForm, id: string) => {
    return update({
      item: {
        ...v,
        id,
        ticketChannelId: v.ticketChannel ? v.ticketChannel.id : "",
        ticketChannelStepId: v.ticketChannelStep?.id!,
        ticketTypeId: v.ticketType.id,
        minutes: Number(v.minutes),
        initialDate: v.initialDate.toISOString(),
        endDate: v.endDate.toISOString(),
      },
      id: id,
    });
  };

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TTicketAutoInactiveRuleForm>> => {
      const { data, success } = await get(id);

      if (data) {
        if (data.ticketChannel && data.ticketChannel.ticketWorkspaceId) {
          setTicketWorkspaceId(data.ticketChannel.ticketWorkspaceId);
        }

        if (data.message && data.message?.length !== 0) {
          setMessage(data.message);
        }

        const newData: TTicketAutoInactiveRuleForm = {
          ...data,
          ticketChannelStep: data.ticketChannelStep ?? null,
          minutes: data.minutes.toString(),
          initialDate: new Date(data.initialDate),
          endDate: new Date(data.endDate),
        };
        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get],
  );

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TTicketAutoInactiveRuleForm>
      saveFunction={handleSave}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "ticket_auto_inactivity_rule")}
      schema={ticketAutoInactiveRuleSchema}
      subtitle={t(translationPath + "new_ticket_auto_inactivity_rule")}
      subtitleWatchField={"ticketType.name"}
      defaultValues={{
        message:
          message.length !== 0
            ? message
            : "Seu atendimento está inativo por muito tempo.",
      }}
      fields={() => (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormInput
              size="small"
              name="minutes"
              type="number"
              fullWidth
              label={t(translationPath + "minutes") || "Minutes"}
            />
          </Grid>

          <TicketType />

          <TicketWorkspace ticketWorkspaceId={ticketWorkspaceId} />

          <TicketChannel />

          <TicketChannelStep />

          <Grid item xs={12} sm={3}>
            <DateInput
              name="initialDate"
              label={t(translationPath + "initial_date")}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <DateInput name="endDate" label={t(translationPath + "end_date")} />
          </Grid>

          <Grid item xs={12} sm={12}>
            <FormInput
              size="small"
              name="message"
              fullWidth
              label={t(translationPath + "message") || "Message"}
              multiline
              minRows={4}
            />
          </Grid>
        </Grid>
      )}
    />
  );
};

export default TicketAutoInactiveRule;
